import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

export default function TalentPoolsPage() {
  const [pools, setPools] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [desc, setDesc] = useState("");
  const [addCandOpen, setAddCandOpen] = useState(false);
  const [cands, setCands] = useState([]); const [candQuery, setCandQuery] = useState("");

  const load = () => api.get("/talent-pools").then(r => setPools(r.data)).catch(() => setPools([]));
  useEffect(() => { load(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setOpen(true);
      const p = new URLSearchParams(searchParams); p.delete("action"); setSearchParams(p, { replace: true });
    }
  }, [searchParams]);

  const openPool = async (p) => {
    setSelected(p);
    const { data } = await api.get(`/talent-pools/${p.pool_id}`);
    setDetail(data);
  };

  const create = async () => {
    if (!name.trim()) return toast.error("Give the pool a name");
    await api.post("/talent-pools", { name, description: desc });
    setOpen(false); setName(""); setDesc("");
    toast.success("Pool created"); load();
  };

  const delPool = async (id) => {
    if (!window.confirm("Delete this pool?")) return;
    await api.delete(`/talent-pools/${id}`);
    setSelected(null); setDetail(null); load(); toast.success("Deleted");
  };

  const searchCands = async () => {
    if (candQuery.length < 2) return;
    const { data } = await api.get(`/search?q=${encodeURIComponent(candQuery)}`);
    setCands(data.results.filter(r => r.type === "candidate"));
  };

  const addCand = async (cid) => {
    await api.post(`/talent-pools/${selected.pool_id}/candidates`, { candidate_id: cid });
    openPool(selected); setAddCandOpen(false); toast.success("Added");
  };

  const removeCand = async (cid) => {
    await api.delete(`/talent-pools/${selected.pool_id}/candidates/${cid}`);
    openPool(selected);
  };

  return (
    <div className="space-y-6" data-testid="talent-pools-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display flex items-center gap-2">
            <Users className="w-6 h-6 text-pink" /> Talent Pools
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Curate lists of strong candidates for future roles.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="btn-primary rounded-full" data-testid="pool-new-btn"><Plus className="w-4 h-4 mr-1" /> New pool</Button>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-2">
          {pools.length === 0 && <div className="text-sm text-neutral-500 p-4 bg-white rounded-lg border">No pools yet — create your first.</div>}
          {pools.map(p => (
            <button key={p.pool_id} onClick={() => openPool(p)}
              data-testid={`pool-row-${p.pool_id}`}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.pool_id===p.pool_id ? "bg-pink/5 border-pink" : "bg-white border-neutral-200 hover:border-pink"}`}>
              <div className="font-semibold text-sm">{p.name}</div>
              <div className="text-xs text-neutral-500 mt-0.5">{p.candidate_ids?.length || 0} candidate(s)</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2">
          {detail ? (
            <Card className="border-0 shadow-sm" data-testid="pool-detail">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{detail.name}</h2>
                    <p className="text-sm text-neutral-500 mt-1">{detail.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setAddCandOpen(true)} data-testid="pool-add-cand"><UserPlus className="w-3.5 h-3.5 mr-1" /> Add candidate</Button>
                    <Button size="sm" variant="outline" onClick={() => delPool(detail.pool_id)} className="text-red-500 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {(detail.candidates || []).length === 0 && <div className="text-sm text-neutral-500 py-6 text-center">No candidates yet.</div>}
                  {detail.candidates?.map(c => (
                    <div key={c.candidate_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg" data-testid={`pool-cand-${c.candidate_id}`}>
                      <div>
                        <div className="text-sm font-semibold">{c.name}</div>
                        <div className="text-xs text-neutral-500">{c.email} · <Badge variant="outline" className="text-[9px]">{c.stage}</Badge></div>
                      </div>
                      <button onClick={() => removeCand(c.candidate_id)} className="text-neutral-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-sm text-neutral-500 text-center py-16">Select a pool on the left to view candidates.</div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pool-new-dialog">
          <DialogHeader><DialogTitle>New talent pool</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Pool name — e.g. Senior Engineers Q1" value={name} onChange={e=>setName(e.target.value)} data-testid="pool-new-name" />
            <Input placeholder="Description (optional)" value={desc} onChange={e=>setDesc(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={create} data-testid="pool-new-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addCandOpen} onOpenChange={setAddCandOpen}>
        <DialogContent data-testid="pool-add-dialog">
          <DialogHeader><DialogTitle>Add candidate to pool</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={candQuery} onChange={e=>setCandQuery(e.target.value)} onKeyDown={e=>e.key==="Enter" && searchCands()} placeholder="Search candidates by name or email" data-testid="pool-search-input" />
            <Button onClick={searchCands}>Search</Button>
          </div>
          <div className="space-y-1 mt-3 max-h-64 overflow-y-auto">
            {cands.map(c => (
              <button key={c.id} onClick={() => addCand(c.id)} className="w-full text-left p-2 hover:bg-neutral-50 rounded-lg" data-testid={`pool-search-result-${c.id}`}>
                <div className="text-sm font-semibold">{c.title}</div>
                <div className="text-xs text-neutral-500">{c.subtitle}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
